import {createDialogOverlay, fillDialog, pvpButton} from "./DialogHelpers.js";
import {hostDialogAvailable, requestHostToolPrompt} from "./HostDialog.js";

import {t} from "./i18n/index.js";
// Modal popup shown when the Pipe tool's cylinder fit is rejected
// specifically because the fit quality is too poor at the default, safe
// tolerance (pipeAnalysis.js's SAFE_REL_RESIDUAL_THRESHOLD - result.code ===
// "fit_quality_too_low"). Offers an explicit, informed retry at a looser
// tolerance (RELAXED_REL_RESIDUAL_THRESHOLD) - never applied silently/
// automatically, and never a persistent setting (see
// digitization/settings/SettingsSchema.js's "algorithm configuration" vs.
// "user preferences" distinction): the user must consciously choose it, and
// is told plainly that the result will be less accurate, each time.
//
// Same shape/pattern as WallThicknessPrompt.js, adapted to a yes/no choice.
// Resolves to:
//   true  - retry with the relaxed tolerance
//   false - decline (Nej / Annuller / ESC / tool switched away) - the
//           original rejection should be shown as usual
export function promptRelaxedFitRetry(dm){
	// Vært-UI (PointViews frontend) viser dialogen; uden vært (standalone Potree) bruges DOM-dialogen herunder.
	if(hostDialogAvailable(dm.viewer)){
		return requestHostToolPrompt(dm.viewer, {
			kind: 'confirm',
			size: 's',
			title: t('Punktskydata passer dårligt til en cylinder'),
			text: t('Den normale beregning kunne ikke finde et sikkert resultat.\n\n') +
				t('Du kan vælge at genberegne med en løsere tolerance. Det giver et resultat, men det vil være mindre nøjagtigt end normalt, og bør kontrolleres i felten.'),
			confirmLabel: t('Genberegn (mindre nøjagtigt)'),
		}).then((result) => result.action === 'confirm');
	}
	return new Promise((resolve) => {
		const {overlay, box} = createDialogOverlay(dm, 320);
		fillDialog(box, {
			title: t(`Punktskydata passer dårligt til en cylinder`),
			body: `<div style="font-size:12px; opacity:0.85; margin-bottom:10px;">${t("Den normale beregning kunne ikke finde et sikkert resultat.")}</div>
			<p class="pvp-desc" style="color:inherit">
				${t("Du kan vælge at genberegne med en løsere tolerance. Det giver et resultat, men det vil være <b>mindre nøjagtigt</b> end normalt, og bør kontrolleres i felten.")}
			</p>`,
			actions: pvpButton('cancel', t("Annuller")) + pvpButton('retry', t("Genberegn (mindre nøjagtigt)"), 'primary'),
		});

		let done = false;
		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};
		const finish = (value) => {
			if(done) return;
			done = true;
			cleanup();
			resolve(value);
		};

		const onCancelEvent = () => finish(false);

		box.querySelector('[data-action="retry"]').addEventListener('click', () => finish(true));
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(false));
		box.setAttribute('tabindex', '-1');
		box.addEventListener('keydown', (e) => {
			// stopPropagation: see WallThicknessPrompt.js's identical comment -
			// keeps DigitizationModule's global Escape handler from double-
			// handling this while the popup is open
			if(e.key === 'Escape'){ e.stopPropagation(); finish(false); }
		});
		box.focus();

		// covers ESC pressed outside the popup, or the tool being switched
		// away from some other way while the popup is still open
		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}
